import React, { useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import { useComponentTypes } from './hooks';

const useStyles = makeStyles(theme => ({
  formControl: {
    width: '100%',
  },
  option: {
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
  },
  inputRoot: {
    display: 'flex',
    alignItems: 'center',
    height: 40,
  },
  input: {
    padding: '0 14px',
    lineHeight: 1.5,
    '&::placeholder': {
      opacity: 0.7,
    },
  },
  listbox: {
    maxHeight: 250,
  },
  loading: {
    marginRight: theme.spacing(1),
  },
  noOptions: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  helperText: {
    marginLeft: 0,
  },
}));

export const ComponentTypePickerSchema = {
  uiOptions: {
    type: 'object',
    properties: {
      title: {
        type: 'string',
        title: 'Title',
        default: 'Component Type',
      },
      description: {
        type: 'string',
        title: 'Description',
        default: 'Select an existing type or enter a new one',
      },
    },
  },
  // String return value
  returnValue: { type: 'string' },
};

export const ComponentTypePicker = (
  props: FieldExtensionComponentProps<
    string,
    { title: string; description: string }
  >,
) => {
  const { onChange, rawErrors, required, formData, uiSchema } = props;
  const classes = useStyles();

  const [inputValue, setInputValue] = useState('');
  const { options, loading } = useComponentTypes();
  const [open, setOpen] = useState(false);

  const title = uiSchema?.['ui:options']?.title || 'Component Type';
  const description =
    uiSchema?.['ui:options']?.description ||
    'Select an existing component type or enter a new one';

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && inputValue && !options.includes(inputValue)) {
      event.preventDefault();
      onChange(inputValue);
      setOpen(false);
    }
  };

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      className={classes.formControl}
    >
      <Autocomplete
        id="component-type-picker"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        inputValue={inputValue}
        onInputChange={(_, newInput) => setInputValue(newInput)}
        onChange={(_, newValue) =>
          onChange(typeof newValue === 'string' ? newValue : newValue || '')
        }
        freeSolo
        options={options}
        loading={loading}
        value={formData || null}
        classes={{
          option: classes.option,
          listbox: classes.listbox,
          noOptions: classes.noOptions,
        }}
        noOptionsText="No component types found"
        loadingText="Loading component types..."
        getOptionLabel={option => option || ''}
        filterOptions={(opts, state) =>
          opts.filter(option =>
            option
              .toLowerCase()
              .includes(state.inputValue.trim().toLowerCase()),
          )
        }
        renderInput={params => (
          <TextField
            {...params}
            fullWidth
            size="small"
            variant="outlined"
            label={title}
            helperText={description}
            FormHelperTextProps={{
              className: classes.helperText,
            }}
            placeholder="Select or enter a component type"
            error={rawErrors?.length > 0 && !formData}
            onKeyDown={handleKeyDown}
            InputProps={{
              ...params.InputProps,
              classes: {
                root: classes.inputRoot,
                input: classes.input,
              },
              endAdornment: (
                <>
                  {loading && (
                    <CircularProgress size={20} className={classes.loading} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </FormControl>
  );
};
